import React, { useCallback, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';

import MessagePropType from '../definitions/MessagePropType';
import getInputTypeByField from '../functions/getInputTypeByField';
import focusNextElementInForm from '../functions/focusNextElementInForm';
import INPUT_TYPES from '../definitions/InputTypes';
import FieldPropType from '../definitions/FieldPropType';
import useForm from '../hooks/useForm';
import useFieldset from '../hooks/useFieldset';
import isFieldRequired from '../functions/isFieldRequired';
import { ALIGNMENT_OPTIONS, LOCATION_OPTIONS } from './Tooltip';
import QuestionContainer from './QuestionContainer';
import InputSelector from './InputSelector';
import { ADDRESS_VARIANTS } from './AddressInput';
import { AnimationTypePropType, ANIMATION_TYPES } from './AnimatedContainer';
import withField from './withField';


function Question({
    field,

    labelMessage,

    tooltipMessage,
    tooltipLocation,
    tooltipAlignment,

    quicktipMessage,

    isVisible = true,
    withColour = true,
    animationType = ANIMATION_TYPES.FADE,

    type,

    className,
    children,
    showOptionalText = true,

    onChange,
    onKeyPress,
    onComplete,
    onValidityChange,

    addressVariant,
    isSideBySide,
    shouldRemoveMargin,

    // aliased to allow us to pull from spread but re-use these names
    isCompleteOnButtonClick: isCompleteOnButtonClickProp,
    isCompleteOnEnter: isCompleteOnEnterProp,

    ...otherProps
}) {
    const form = useForm();
    const fieldset = useFieldset();
    const [ createdField, setCreatedField ] = useState(null); // config -> instance.
    // memoized to avoid infinite re-renders
    const CustomInput = useMemo(
        () => typeof type !== 'string' && !!type // typeof type === 'function' doesn't work for memoized components
            ? withField(type)
            : null,
        [ type ],
    );
    const inputType = typeof type === 'string' || !type // Type should either be a string or optional
        ? getInputTypeByField(field, type)
        : null;

    const isCompleteOnButtonClick = isCompleteOnButtonClickProp ?? getIsCompleteOnButtonClickByInputType(inputType);
    const isCompleteOnEnter = isCompleteOnEnterProp ?? getIsCompleteOnEnterByInputType(inputType);

    const tooltipId = field.name + '-tooltip';

    const [ shouldShowInvalid, setShouldShowInvalid ] = useState(false);
    const [ hasAttemptedComplete, setHasAttemptedComplete ] = useState(false);

    const isAutoFocusEnabled = fieldset ? fieldset.isAutoFocusEnabled : !!form?.isAutoFocusEnabled;


    // We want to handle the field state visibility ourselves, since we're going to do visibility
    // animation at the QuestionContainer-level. We don't want to worry about animation synchronization.
    useEffect(() => {
        if (inputType !== INPUT_TYPES.CHECKBOX) {
            form?.setFieldVisibility(field.name, isVisible);
        }
    }, [ isVisible, form, field.name ]);

    function handleChange(e) {
        onChange?.(e);

        if (!isCompleteOnButtonClick) {
            handleComplete();
        }
    }

    function handleKeyPress(e) {
        onKeyPress?.(e);

        if (e.key === 'Enter' && isCompleteOnEnter) {
            handleComplete();
        }
    }

    function handleComplete() {
        if (!isAutoFocusEnabled) {
            return;
        }

        setHasAttemptedComplete(true); // if triggered by enter keypress, input would not be blurred and wouldn't trigger validation

        if (!createdField?.isValid) {
            return false;
        }

        onComplete?.();

        focusNextElementInForm(createdField.name);
    }

    // Memoized because inputs expect a stable reference; if we don't memoize, it'll re-raise validity every render.
    const handleValidityChange = useCallback(
        (validityState) => {
            setShouldShowInvalid(validityState.isShowingInvalid);
            onValidityChange?.(validityState);
        },
        [ setShouldShowInvalid, onValidityChange ],
    );

    return (
        <Choose>
            <When condition={inputType === INPUT_TYPES.CHECKBOX}>
                <InputSelector
                    field={field}
                    type={type}
                    isVisible={isVisible}
                    label={labelMessage}
                    className={className}
                    {...otherProps}
                    data-question-name={field.name}
                    onCreateField={setCreatedField}
                    onValidityChange={handleValidityChange}
                    canShowInvalid={hasAttemptedComplete}
                />
            </When>
            <Otherwise>
                <QuestionContainer
                    labelFor={field.name}
                    labelMessage={labelMessage}
                    tooltipId={tooltipId}
                    tooltipMessage={tooltipMessage}
                    tooltipLocation={tooltipLocation}
                    tooltipAlignment={tooltipAlignment}
                    quicktipMessage={quicktipMessage}
                    isVisible={isVisible}
                    withColour={withColour}
                    animationType={animationType}
                    isInvalid={shouldShowInvalid}
                    className={className}
                    isOptional={isFieldRequired(field, form) === false}
                    showOptionalText={showOptionalText}
                    /* REQUIREMENT: Wide addresses always display full width */
                    isSideBySide={
                        inputType === INPUT_TYPES.ADDRESS && addressVariant === ADDRESS_VARIANTS.WIDE
                            ? false
                            : isSideBySide
                    }
                    shouldRemoveMargin={shouldRemoveMargin}
                    data-question-name={field.name}
                    onCompleteButtonClick={
                        isAutoFocusEnabled
                        && !shouldShowInvalid
                        && isCompleteOnButtonClick
                            ? handleComplete
                            : undefined
                    }
                    showCompleteButtonEnterText={isCompleteOnEnter}
                    /* REQUIREMENT: For address input, always show the next button regardless of focus */
                    forceCompleteButtonVisibility={inputType === INPUT_TYPES.ADDRESS}
                >
                    <Choose>
                        <When condition={!!CustomInput}>
                            <CustomInput
                                field={field}
                                aria-describedby={tooltipId}
                                onChange={handleChange}
                                onKeyPress={handleKeyPress}
                                {...otherProps}
                                onCreateField={setCreatedField}
                                onValidityChange={handleValidityChange}
                                canShowInvalid={hasAttemptedComplete}
                            />
                        </When>
                        <Otherwise>
                            <InputSelector
                                field={field}
                                type={type}
                                aria-describedby={tooltipId}
                                onChange={handleChange}
                                onKeyPress={handleKeyPress}
                                variant={
                                    inputType === INPUT_TYPES.ADDRESS && addressVariant === ADDRESS_VARIANTS.WIDE
                                        ? ADDRESS_VARIANTS.WIDE
                                        : undefined
                                }
                                {...otherProps}
                                onCreateField={setCreatedField}
                                onValidityChange={handleValidityChange}
                                canShowInvalid={hasAttemptedComplete}
                            />
                        </Otherwise>
                    </Choose>
                    {children}
                </QuestionContainer>
            </Otherwise>
        </Choose>
    );
}

Question.propTypes = {
    field: FieldPropType.isRequired,

    labelMessage: MessagePropType.isRequired,

    tooltipMessage: MessagePropType,
    tooltipLocation: PropTypes.oneOf(LOCATION_OPTIONS),
    tooltipAlignment: PropTypes.oneOf(ALIGNMENT_OPTIONS),

    quicktipMessage: MessagePropType,

    isVisible: PropTypes.bool,
    withColour: PropTypes.bool,
    animationType: AnimationTypePropType,

    className: PropTypes.string,
    type: PropTypes.oneOfType([
        PropTypes.oneOf(Object.values(INPUT_TYPES)),
        PropTypes.elementType,
    ]),
    children: PropTypes.node,
    showOptionalText: PropTypes.bool,

    onChange: PropTypes.func,
    onKeyPress: PropTypes.func,
    onComplete: PropTypes.func,
    onValidityChange: PropTypes.func,

    addressVariant: PropTypes.oneOf(Object.values(ADDRESS_VARIANTS)),
    isSideBySide: PropTypes.bool,
    shouldRemoveMargin: PropTypes.bool,

    isCompleteOnButtonClick: PropTypes.bool,
    isCompleteOnEnter: PropTypes.bool,
};


/**
 * @param {string | null} inputType - if passed null, always false
 * @returns {boolean}
 */
function getIsCompleteOnButtonClickByInputType(inputType) {
    return [
        INPUT_TYPES.ADDRESS,
        INPUT_TYPES.CHECKBOX,
        INPUT_TYPES.CHECKBOX_EXPANDABLE,
        INPUT_TYPES.CURRENCY,
        INPUT_TYPES.DATE,
        INPUT_TYPES.EMAIL,
        INPUT_TYPES.NUMBER,
        INPUT_TYPES.PERCENT,
        INPUT_TYPES.PHONE,
        INPUT_TYPES.SELECTOR,
        INPUT_TYPES.TEXT,
        INPUT_TYPES.TEXTAREA,
    ].includes(inputType);
}

/**
 * @param {string | null} inputType - if passed null, always false
 * @returns {boolean}
 */
function getIsCompleteOnEnterByInputType(inputType) {
    return [
        INPUT_TYPES.CHECKBOX,
        INPUT_TYPES.CHECKBOX_EXPANDABLE,
        INPUT_TYPES.CURRENCY,
        INPUT_TYPES.DATE,
        INPUT_TYPES.EMAIL,
        INPUT_TYPES.NUMBER,
        INPUT_TYPES.PERCENT,
        INPUT_TYPES.PHONE,
        INPUT_TYPES.TEXT,
        INPUT_TYPES.RADIO,
    ].includes(inputType);
}

export default observer(Question);
